import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpResponse, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { ScheduledDashboardEmail } from '../types';

type ScheduledDashboardEmailResponse = {
    scheduledDashboardEmails: ScheduledDashboardEmail[];
};

export const useGetScheduledDashboardEmails = (
    dashboardKey: number,
    method: 'self' | 'other',
    isSupport: boolean,
) =>
    useQuery({
        queryKey: ['scheduled_dashboard_emails', dashboardKey, method],

        queryFn: async () => {
            const response =
                await RQAPICallWithData<ScheduledDashboardEmailResponse>({
                    endpoint: `explore/scheduled_dashboard_emails/${dashboardKey}/${method}`,
                    method: 'GET',
                });
            return response.content.data.scheduledDashboardEmails;
        },

        enabled: method === 'self' || isSupport,
    });

type MutationProps = { id: number };
/**
 * This hook force send a schedule dashboard email
 */
export const useForceSendDashboardMutation = () => {
    const dispatch = useDispatch();
    return useMutation<HttpResponse<object>, Error, MutationProps>({
        mutationFn: ({ id }) =>
            RQAPICall({
                endpoint: `explore/scheduled_dashboard_email/${id}/send`,
                method: 'POST',
            }),

        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: data.content.message || '',
                snackType: 'success',
            });
        },

        onError: (error) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};

export const useDeleteScheduleEmailMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<HttpResponse<object>, Error, MutationProps>({
        mutationFn: ({ id }) =>
            RQAPICall({
                endpoint: `explore/scheduled_dashboard_email/${id}`,
                method: 'DELETE',
            }),

        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['scheduled_dashboard_emails'],
            }),
    });
};

/**
 * Hook use to edit the scheduled email configuration.
 * @params {number} dashboardKey - The key of the dashboard to edit.
 */
export const useEditScheduleDashboardEmailConfigMutation = (
    dashboardKey: number,
) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return useMutation<HttpResponse<object>, Error, ScheduledDashboardEmail>({
        mutationFn: (payload: ScheduledDashboardEmail) =>
            RQAPICall({
                endpoint: `explore/scheduled_dashboard_email/${payload.id}`,
                method: 'PUT',
                body: payload,
            }),

        onSuccess: () => {
            dispatch.vision.notifyUser({
                message: t('sendDashboard.editSuccess'),
                snackType: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: ['scheduled_dashboard_emails', dashboardKey],
            });
        },

        onError: (error) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};

/**
 * Hook to add a new scheduled dashboard email configuration.
 * @param {number} dashboardKey - The key of the dashboard to add the schedule for.
 */
export const useAddScheduleDashboardEmailConfigMutation = (
    dashboardKey: number,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (scheduledEmail: ScheduledDashboardEmail) =>
            RQAPICall({
                endpoint: `explore/scheduled_dashboard_emails/${dashboardKey}`,
                method: 'POST',
                body: scheduledEmail,
            }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['scheduled_dashboard_emails', dashboardKey],
            });
        },
    });
};
